import type { ClipBriefRequest, ClipPublicGrowthPayload, ClipPublicRemixRequest } from '~/services/musicService';

export const CLIP_REMIX_DRAFT_STORAGE_KEY = 'musicmint:clips:remix-draft:v1';
const CLIP_REMIX_DRAFT_TTL_MS = 1000 * 60 * 60 * 24;

export interface ClipRemixDraft extends ClipBriefRequest {
  /** Private review drafts stay with their creator; public/template drafts omit this. */
  owner_id?: string;
  source_package_id?: string;
  source_template_id?: string;
  created_at: number;
}

type StorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

export const buildClipRemixDraft = (
  growth: Pick<ClipPublicGrowthPayload, 'package_id' | 'template_id' | 'remix_request'>,
  now = Date.now(),
): ClipRemixDraft => clipRemixRequestToDraft(growth.remix_request, {
  source_package_id: growth.package_id || growth.remix_request.source_package_id,
  source_template_id: growth.template_id,
  created_at: now,
});

export const buildClipTemplateDraft = (
  remixRequest: ClipPublicRemixRequest,
  templateId: string,
  now = Date.now(),
): ClipRemixDraft => clipRemixRequestToDraft(remixRequest, {
  source_template_id: templateId,
  created_at: now,
});

export const saveClipRemixDraft = (draft: ClipRemixDraft, storage: StorageLike = window.sessionStorage) => {
  storage.setItem(CLIP_REMIX_DRAFT_STORAGE_KEY, JSON.stringify(draft));
};

export const parseClipRemixDraft = (raw: string | null, now = Date.now()): ClipRemixDraft | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ClipRemixDraft;
    if (!parsed.script_or_brief || !parsed.created_at) return null;
    if (now - parsed.created_at > CLIP_REMIX_DRAFT_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const consumeClipRemixDraft = (storage: StorageLike = window.sessionStorage, now = Date.now(), ownerId = ''): ClipRemixDraft | null => {
  const draft = parseClipRemixDraft(storage.getItem(CLIP_REMIX_DRAFT_STORAGE_KEY), now);
  storage.removeItem(CLIP_REMIX_DRAFT_STORAGE_KEY);
  return draft?.owner_id && draft.owner_id !== ownerId ? null : draft;
};

const clipRemixRequestToDraft = (
  request: ClipPublicRemixRequest,
  extra: Pick<ClipRemixDraft, 'created_at' | 'source_package_id' | 'source_template_id'>,
): ClipRemixDraft => ({
  script_or_brief: request.script_or_brief,
  platform: request.platform || 'TikTok',
  use_case: request.use_case || 'product_ad',
  duration: request.duration || '20s',
  mood: request.mood || '',
  style: request.style || '',
  language: request.language || 'English',
  brand_or_product_name: request.brand_or_product_name || '',
  needs_vertical_video: request.needs_vertical_video ?? true,
  needs_commercial_license: request.needs_commercial_license ?? false,
  reference_url: request.reference_url || '',
  source_package_id: extra.source_package_id,
  source_template_id: extra.source_template_id,
  created_at: extra.created_at,
});
